import React, { useCallback, useContext, useState } from 'react';
import { useParams } from 'next/navigation';
import { Transition } from '@headlessui/react';
import { XMarkIcon as CloseIcon } from '@heroicons/react/24/solid';
import Image from 'components/commercetools-ui/atoms/image';
import Link from 'components/commercetools-ui/atoms/link';
import Overlay from 'components/commercetools-ui/atoms/overlay';
import Typography from 'components/commercetools-ui/atoms/typography';
import ProductVariantPrice from 'components/commercetools-ui/organisms/product/product-details/components/product-variant-price';
import ProductSlider from 'components/commercetools-ui/organisms/product/product-slider';
import BundleIcon from 'components/icons/bundle-icon';
import EbookIcon from 'components/icons/ebook-icon';
import PhysicalBookIcon from 'components/icons/physical-book-icon';
import { useFormat } from 'helpers/hooks/useFormat';
import useScrollBlock from 'helpers/hooks/useScrollBlock';
import useTouchDevice from 'helpers/hooks/useTouchDevice';
import { mediumDesktop, tablet } from 'helpers/utils/screensizes';
import { Product, Variant } from 'types/entity/product';
import { useCart, useProduct, useWishlist } from 'frontastic';
import { AddToCartOverlayContextShape, StateProduct } from './types';
import { getDiscount } from 'helpers/utils/getDiscount';
import DiscountBadge from 'components/commercetools-ui/molecules/badge/DiscountBadge';
import Button from 'components/commercetools-ui/atoms/button';

const AddToCartOverlayContext = React.createContext<AddToCartOverlayContextShape>({
  show() {},
  hide() {},
  fetchRelatedProducts() {
    return new Promise((res) => res());
  },
});

const AddToCartOverlayProvider = ({ children }: React.PropsWithChildren) => {
  const { locale } = useParams();

  const { data, addToWishlist, removeLineItem } = useWishlist();

  const { shippingMethods } = useCart();

  const { isTouchDevice } = useTouchDevice();

  const { formatMessage: formatProductMessage } = useFormat({ name: 'product' });
  const { formatMessage: formatCartMessage } = useFormat({ name: 'cart' });

  const { blockScroll } = useScrollBlock();

  const { query } = useProduct();

  const [product, setProduct] = useState<StateProduct>();

  const [relatedProducts, setRelatedProducts] = useState<Product[]>([]);

  const fetchRelatedProducts = useCallback(
    async (product: Product) => {
      const category = product?.categories?.[0]?.categoryId;

      if (!category) return;

      const response = await query({ categories: [category], limit: 15 });

      if (response.isError) return;

      setRelatedProducts(response.data.items as Product[]);
    },
    [query],
  );

  const show = useCallback(
    (product: Product, variant: Variant, addedQty: number, intendedQty: number) => {
      setProduct({ ...product, ...variant, addedQty, intendedQty });
      blockScroll(true);
    },
    [blockScroll],
  );

  const hide = useCallback(() => {
    setProduct(undefined);
    blockScroll(false);
  }, [blockScroll]);

  const { hasDiscount, discountPercentage } = getDiscount(product ?? ({} as Variant));

  return (
    <>
      {product && <Overlay onClick={hide} />}
      <Transition
        show={!!product}
        className="fixed bottom-0 z-[9999] w-full overflow-hidden rounded-[20px_20px_0_0] bg-white shadow-md md:bottom-[unset] md:left-1/2 md:top-1/2 md:w-[90%] md:max-w-[650px] md:-translate-x-1/2 md:-translate-y-1/2 md:rounded-lg"
        enter="transition md:transition-opacity duration-75"
        enterFrom="opacity-0 translate-y-full"
        enterTo="opacity-100 translate-y-0"
        leave="transition md:transition-opacity duration-150"
        leaveFrom="opacity-100 translate-y-0"
        leaveTo="opacity-0 translate-y-full"
      >
        <div onMouseUp={(e) => e.stopPropagation()}>
          <div className="bg-white p-16 md:px-48 md:py-24">
            {(product?.addedQty ?? 0) === (product?.intendedQty ?? 0) ? (
              <h4 className="text-18 leading-[27px] lg:text-22 lg:leading-[33px]">
                {formatProductMessage({ id: 'cart.added', defaultMessage: 'Added to cart' })}
              </h4>
            ) : (
              <h4 className="text-18 font-semibold leading-[27px] text-blue-600 lg:text-22 lg:leading-[33px]">
                {formatProductMessage({ id: 'already.in.cart', defaultMessage: 'Already in cart' })}
              </h4>
            )}

            <CloseIcon
              className="absolute right-18 top-16 size-28 cursor-pointer fill-secondary-black stroke-0 md:top-16"
              onClick={hide}
            />

            <div className="mt-22 md:mt-35 lg:mt-30">
              <div className="flex max-w-full items-stretch justify-start gap-10 p-16 md:gap-15">
                <div className="w-125 shrink-0 bg-white p-12">
                  <div className="relative size-full rounded-sm">
                    {product?.images?.[0] ? (
                      <Image src={product?.images?.[0]} suffix="small" style={{ objectFit: 'contain' }} fill />
                    ) : (
                      <Image
                        className="w-full rounded-sm object-contain group-hover:opacity-75"
                        src="/images/imagen.png"
                        alt="Product coming soon image in cart"
                      />
                    )}
                    <div className="absolute left-0 top-0 z-10 w-full">
                      <div className="w-full text-center">
                        {hasDiscount && <DiscountBadge discountPercentage={discountPercentage} />}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="max-w-full grow overflow-hidden">
                  <div className="flex items-center gap-6">
                    {product?.productTypeKey === 'physical-book' ? (
                      <div className="flex items-center gap-6">
                        <PhysicalBookIcon className="size-14" />
                        <Typography className="text-xs"> Printed Book</Typography>
                      </div>
                    ) : product?.productTypeKey === 'ebook' ? (
                      <div className="flex items-center gap-6">
                        <EbookIcon className="size-14" />
                        <Typography className="text-xs">eBook</Typography>
                      </div>
                    ) : product?.productTypeKey === 'bundle' ? (
                      <div className="flex items-center gap-6">
                        <BundleIcon className="size-14" />
                        <Typography className="text-xs">Book & eBook</Typography>
                      </div>
                    ) : (
                      <div className="flex items-center gap-6">
                        <Typography className="text-xs">
                          {product?.productTypeKey
                            ?.replace(/-/g, ' ')
                            .split(' ')
                            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                            .join(' ')}
                        </Typography>
                      </div>
                    )}
                  </div>
                  <Link
                    link={product?._url}
                    className="line-clamp-2 overflow-hidden text-ellipsis text-sm font-bold leading-normal text-blue-600 underline-offset-4 hover:underline focus:underline active:underline md:mt-10 md:text-base lg:text-lg"
                    title={product?.name}
                  >
                    {product?.name}
                  </Link>

                  <div className="relative mt-2 flex flex-wrap pr-40 text-base font-normal leading-none text-gray-500">
                    {product?.attributes?.['author'] && (
                      <>
                        <Typography className="mr-4 text-xs md:text-sm">Author:</Typography>
                        <Typography className="mr-8 whitespace-nowrap border-r border-gray-500 pr-8 text-xs text-blue-600 md:text-sm">
                          {product.attributes?.['author']}
                        </Typography>
                      </>
                    )}
                    {(product?.attributes?.['publicationYear'] ||
                      product?.attributes?.['publicationMonth'] ||
                      product?.attributes?.['publicationDay']) && (
                      <>
                        <Typography className="mr-4 whitespace-nowrap text-xs md:text-sm">Publication date:</Typography>
                        <Typography className="whitespace-nowrap text-xs text-primary-black md:text-sm">
                          {[
                            `${product?.attributes?.['publicationYear']}`,
                            product?.attributes?.['publicationMonth'] && product?.attributes?.['publicationMonth'],
                            product?.attributes?.['publicationDay'] && product?.attributes?.['publicationDay'],
                          ]
                            .filter(Boolean)
                            .join('-')}
                        </Typography>
                      </>
                    )}
                  </div>

                  <div className="flex items-end justify-between gap-8 pt-20">
                    <ProductVariantPrice variant={product} inModalVersion className="text-base" />
                    {(product?.addedQty ?? 0) !== (product?.intendedQty ?? 0) && (
                      <Typography className="text-12 font-bold italic md:text-14">
                        {formatProductMessage({
                          id: 'max.quantity.reached',
                          defaultMessage: '{qty} items in cart',
                          values: { qty: 99 },
                        })}
                      </Typography>
                    )}
                  </div>
                  {(product?.addedQty ?? 0) !== (product?.intendedQty ?? 0) && (
                    <div className="mt-24">
                      <Typography className="text-12 font-semibold text-red-600 md:text-14">
                        {formatProductMessage({
                          id: 'quantity.exceeded',
                          defaultMessage: 'For bulk orders of 100 copies or more, call us.',
                        })}
                      </Typography>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-18 flex w-full flex-col gap-16 md:mt-36 md:flex-row-reverse">
              <Link link="/cart" onClick={hide} className="md:flex-1">
                <Button variant="primary" className="w-full">
                  {formatCartMessage({ id: 'cart.go', defaultMessage: 'Go to cart' })}
                </Button>
              </Link>
              <div onClick={hide} className="md:flex-1">
                <Button variant="secondary" className="w-full">
                  {formatCartMessage({ id: 'continue.shopping', defaultMessage: 'Continue shopping' })}
                </Button>
              </div>
            </div>
          </div>
          <div className={`mt-36 hidden bg-neutral-200 py-24 md:block ${isTouchDevice ? 'px-48' : 'px-96'}`}>
            <ProductSlider
              clearDefaultWrapperStyles
              products={relatedProducts}
              wishlist={data}
              shippingMethods={shippingMethods.data}
              addToWishlist={async (lineItem, count) => {
                if (data) await addToWishlist(data, lineItem, count);
              }}
              removeLineItem={async (lineItem) => {
                if (data) await removeLineItem(data, lineItem);
              }}
              title={formatProductMessage({ id: 'bought.together', defaultMessage: 'Frequently bought together' })}
              titleVariant="xs"
              innerArrows={false}
              solidArrows={false}
              disableProductQuickView
              disableProductWishlistButton
              disableProductVariants
              onProductClick={hide}
              classNames={{ title: 'text-center' }}
              spaceBetween={10}
              slidesPerGroup={3}
              slidesPerView={3}
              breakpoints={{
                [tablet]: {
                  slidesPerView: 3,
                  slidesPerGroup: 3,
                  spaceBetween: 10,
                },
                [mediumDesktop]: {
                  slidesPerView: 3,
                  slidesPerGroup: 3,
                  spaceBetween: 10,
                },
              }}
            />
          </div>
        </div>
      </Transition>
      <AddToCartOverlayContext.Provider value={{ show, hide, fetchRelatedProducts }}>
        {children}
      </AddToCartOverlayContext.Provider>
    </>
  );
};

export default AddToCartOverlayProvider;

export const useAddToCartOverlay = () => useContext(AddToCartOverlayContext);
